import { useCallback, useEffect, useState } from 'react';
import normalizeVariables from '../../../graphql/schema/normalizeVariables';
import validationImport from '../../../graphql/schema/validation.json';
import deepEqual from '../../../components/connectedForm/utils/deepEqual';
import { EntityType } from '../../../@types/entityTypes';

//List of transient fields that should flag pristine (e.g. Deleted should be included because it's set by the user in the UI).
const PERMITTED_TRANSIENT_FIELDS = ['deleted'];
const validation = validationImport as {
  [typeKey: string]: {
    [fieldName: string]: {
      name: string;
      type: string;
      isEntity: boolean;
      pattern: any;
      mandatory: boolean;
      minValue: null | string;
      maxValue: null | string;
      readOnly: boolean;
      isTransient: boolean;
      isCalculated: boolean;
      typeParameters: Array<any>;
    };
  };
};

const runPristineEqualsCheck = (
  previous: any,
  current: any,
  type: string,
  { debug = false, pristineWhenIdIsNull = false } = {}
) => {
  if (!pristineWhenIdIsNull && !current?.id) return false; //If the entity has no id, it cannot be considered pristine as it is not yet saved.

  if (debug) return pristineEqualsCheckDebug(previous, current, type);

  return pristineEqualsCheck(previous, current, type);
};

//Custom isEquals using validation.json to check for transients.
export const pristineEqualsCheck = (
  previous: any,
  current: any,
  type: string,
  { ignoreFieldKeys = [] }: { ignoreFieldKeys?: Array<string> } = {}
): boolean => {
  if (validation[type]) {
    return Object.keys(validation[type]).every((key) => {
      const currentValidation = validation[type][key];
      const shouldIgnore =
        (currentValidation.isTransient || currentValidation.isCalculated) && !PERMITTED_TRANSIENT_FIELDS.includes(key);
      if (shouldIgnore || ignoreFieldKeys.includes(key)) {
        return true; //Treat transient as equal - Generated fields shouldn't count as 'dirty'.
      }
      let previousField;
      let currentField: any;
      if (currentValidation?.type === 'BigDecimal') {
        previousField = previous?.[key]?.toString();
        currentField = current?.[key]?.toString();
      } else {
        previousField = previous?.[key];
        currentField = current?.[key];
      }

      if ((currentValidation.isEntity || validation[currentValidation.type]) && (previousField || currentField)) {
        //Process child entity by it's type.
        return pristineEqualsCheck(previousField, currentField, currentValidation.type, { ignoreFieldKeys });
      }
      if (currentValidation.type === 'List' && currentValidation.typeParameters.length > 0) {
        //Process list elements, comparing each at it's current index.
        if (previousField && currentField) {
          if (previousField.length !== currentField.length) {
            return false;
          }

          return previousField.every((previousEntry: any, index: number) => {
            return currentValidation.typeParameters.every((typeKey) => {
              return pristineEqualsCheck(previousEntry, currentField[index], typeKey, { ignoreFieldKeys });
            });
          });
        }
        //One of the child arrays doesn't exist, can just compare exists vs not-exists.
        return !previousField === !currentField;
      }
      return deepEqual(previousField, currentField);
    });
  }
  return deepEqual(previous, current);
};

//DEBUGGING variant of the pristineEqualsCheck above.
//Just reports out to the console where the field difference is.
const pristineEqualsCheckDebug = (previous: any, current: any, type: string, path = 'values'): boolean => {
  if (validation[type]) {
    return Object.keys(validation[type]).every((key) => {
      const currentValidation = validation[type][key];
      if (currentValidation.isTransient || currentValidation.isCalculated) return true; //Treat transient/calculated as equal - Generated fields shouldn't count as 'dirty'.
      //If type of field is numeric, convert it to strings for comparison because 500 becoming "500" is not cause for a save being required.
      let previousField: any;
      let currentField: any;
      if (currentValidation?.type === 'BigDecimal') {
        previousField = previous?.[key]?.toString();
        currentField = current?.[key]?.toString();
      } else {
        previousField = previous?.[key];
        currentField = current?.[key];
      }

      if ((currentValidation.isEntity || validation[currentValidation.type]) && (previousField || currentField)) {
        //Process child entity by it's type.
        const entityResult = pristineEqualsCheckDebug(
          previousField,
          currentField,
          currentValidation.type,
          `${path}.${key}`
        );
        if (!entityResult) {
          console.debug(
            'Difference (Entity)',
            previousField,
            currentField,
            'Type:',
            currentValidation.type,
            'Path:',
            `${path}.${key}`
          );
        }
        return entityResult;
      }
      if (currentValidation.type === 'List' && currentValidation.typeParameters.length > 0) {
        //Process list elements, comparing each at it's current index.
        if (previousField && currentField) {
          if (previousField.length !== currentField.length) {
            console.debug(
              'Difference (List Entry) - Length has changed',
              previousField.length,
              currentField.length,
              'Type:',
              currentValidation.type,
              'Path:',
              `${path}.${key}`
            );
            return false;
          }

          return previousField.every((previousEntry: any, index: number) => {
            return currentValidation.typeParameters.every((typeKey) => {
              const listEntryResult = pristineEqualsCheckDebug(
                previousEntry,
                currentField[index],
                typeKey,
                `${path}.${key}.${index}`
              );
              if (!listEntryResult) {
                console.debug(
                  'Difference (List Entry)',
                  previousEntry,
                  currentField[index],
                  'Type:',
                  currentValidation.type,
                  'Path:',
                  `${path}.${key}`
                );
              }
              return listEntryResult;
            });
          });
        }
        //One of the child arrays doesn't exist, can just compare exists vs not-exists.
        return !previousField === !currentField;
      }

      const deepCompare = deepEqual(previousField, currentField);
      if (!deepCompare) {
        console.debug(
          'Difference (Non-Entity)',
          {
            previousField,
            currentField,
          },
          {
            type: type,
            path: `${path}.${key}`,
            currentValidation,
          }
        );
      }

      return deepCompare;
    });
  }
  console.debug('No validation for type', type, 'returning deepEqual');
  return deepEqual(previous, current);
};

export const usePristineEntity: (pristineEntityOptions: {
  values: any;
  entity?: EntityType;
  customPristineDependency?: (values: any) => any;
}) => [isPristine: boolean, baseValue: any] = ({
  values,
  entity: { type, pristineWhenIdIsNull, pristineCheckIgnoreFieldNames = [] } = {} as EntityType,
  customPristineDependency,
}) => {
  //Base value for replacement 'dirty'/'pristine' flags.
  const [baseValue, setBaseValue] = useState(values);

  const normalizeForPristine = useCallback(
    (values) => {
      //Changes to entityMetaData are not an indication of user changes.
      const normalizeFieldNames = ['entityMetaData', ...pristineCheckIgnoreFieldNames];
      const normalValues = normalizeVariables(values, normalizeFieldNames);
      if (normalValues) {
        //We need to remove 'deleted' from the top-level only - Nested 'deleted' tags indicate a selected deletion operation and are non-pristine, but top level is the
        // entity itself, and is always triggered from an action, so we don't want to trigger the 'leave without saving' prompt from it.
        delete normalValues.deleted;
      }
      return normalValues;
    },
    [pristineCheckIgnoreFieldNames]
  );

  const additionalDependency = customPristineDependency?.(values) || false;

  useEffect(() => {
    //When we save, concurrencyVersion is updated so we need to reset our comparison point.
    setBaseValue(normalizeForPristine(values));

    // Suppression required for exhaustive-deps here - We only want to reset our base value if id/uuid/concurrencyVersion update.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values?.id, values?.uuid, values?.concurrencyVersion, additionalDependency]);

  const normalizedValues = normalizeForPristine(values);

  const pristine = type
    ? runPristineEqualsCheck(baseValue, normalizedValues, type, { debug: false, pristineWhenIdIsNull })
    : deepEqual(baseValue, normalizedValues);

  return [pristine, baseValue];
};
